import { useCallback, useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { grantEligibilityOverride, type GrantOverrideBody } from '../dispatch.api';

/** Recording one override. A refusal stays inline on the form (the server says why: legal rule, not blocking, same person); only a recorded override toasts. */
export function useEligibilityOverride(tripId: string, onGranted: () => void) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const grant = useCallback(
    async (body: GrantOverrideBody, resourceLabel: string): Promise<boolean> => {
      setError(null);
      setSubmitting(true);
      try {
        await grantEligibilityOverride(tripId, body);
        toast.success(`Override recorded for ${resourceLabel}`);
        onGranted();
        return true;
      } catch (err) {
        setError(apiErrorMessage(err) ?? 'Could not record the override — try again.');
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [tripId, onGranted, toast],
  );

  return { grant, submitting, error, resetError: () => setError(null) };
}
